import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./FundsDepositPage.css";
import "./FundsWithdrawalPage.css";
import "./DepositSummaryByCurrencyPage.css";

const STATUS_OPTIONS = [
  { value: "Completed", label: "Completed" },
  { value: "Pending", label: "Pending" },
  { value: "Confirming", label: "Confirming" },
  { value: "Failed", label: "Failed" },
  { value: "all", label: "All" },
];

const CREDITED_OPTIONS = [
  { value: "", label: "Any" },
  { value: "true", label: "Credited only" },
];

const formatNum = (n) =>
  n != null && !Number.isNaN(Number(n))
    ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 12 })
    : "—";

const DepositSummaryByCurrencyPage = () => {
  const [byCurrency, setByCurrency] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const [status, setStatus] = useState("Completed");
  const [credited, setCredited] = useState("");
  const [assetId, setAssetId] = useState("");
  const [chainId, setChainId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const buildParams = useCallback(() => {
    const params = {};
    if (status.trim()) params.status = status.trim();
    if (credited.trim()) params.credited = credited.trim();
    if (assetId.trim()) params.assetId = assetId.trim();
    if (chainId.trim()) params.chainId = chainId.trim();
    if (from.trim()) params.from = from.trim();
    if (to.trim()) params.to = to.trim();
    return params;
  }, [status, credited, assetId, chainId, from, to]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await AdminService.getCoboDepositSummaryByCurrency(buildParams());
      if (res?.success === false) {
        setByCurrency([]);
        setSummary(null);
        setError(res?.message || "Failed to load deposit summary.");
        return;
      }
      const data = res?.data || {};
      const list = Array.isArray(data.by_currency) ? data.by_currency : [];
      setByCurrency(list);
      setSummary(data.summary || null);
      setExpanded({});
    } catch (err) {
      setByCurrency([]);
      setSummary(null);
      setError(
        err?.response?.data?.message || err?.message || "Failed to load deposit summary."
      );
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const toggleExpand = (currency) => {
    setExpanded((prev) => ({ ...prev, [currency]: !prev[currency] }));
  };

  const handleExportExcel = () => {
    setExportLoading(true);
    try {
      if (!byCurrency.length) {
        setToast({ type: "error", message: "No deposit summary data to export." });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const currencyRows = byCurrency.map((row) => ({
        Currency: row.currency ?? "",
        "Total Amount": row.total_amount ?? "",
        Count: row.count ?? "",
        "Credited Count": row.credited_count ?? "",
        Chains: Array.isArray(row.by_chain) ? row.by_chain.length : 0,
      }));

      const chainRows = [];
      byCurrency.forEach((row) => {
        const chains = Array.isArray(row.by_chain) ? row.by_chain : [];
        chains.forEach((ch) => {
          chainRows.push({
            Currency: row.currency ?? "",
            "Chain ID": ch.chain_id ?? "",
            "Total Amount": ch.total_amount ?? "",
            Count: ch.count ?? "",
            "Credited Count": ch.credited_count ?? "",
          });
        });
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(currencyRows),
        "By Currency"
      );
      if (chainRows.length) {
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(chainRows),
          "By Chain"
        );
      }

      if (summary) {
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet([
            {
              "Currencies Count": summary.currencies_count ?? "",
              "Total Deposits": summary.total_deposits ?? "",
              "Credited Deposits": summary.credited_deposits ?? "",
            },
          ]),
          "Summary"
        );
      }

      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `deposit_summary_by_currency_${dateStamp}.xlsx`);

      setToast({
        type: "success",
        message: `Exported ${byCurrency.length} currency row(s) to Excel.`,
      });
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      console.error(e);
      setToast({
        type: "error",
        message: e?.message || "Failed to export Excel file.",
      });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/fundsDManagement">Deposit</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Deposit Summary</span>
          </p>
          <h1 className="dashboard-hero__title">Deposit Summary</h1>
          <p className="dashboard-hero__subtitle">
            Currency-wise Cobo deposit totals with chain breakdown
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-chart-pie" />
        </div>
      </section>

      {toast && (
        <div className={`withdrawal-toast withdrawal-toast--${toast.type}`} role="status">
          <i className={`fas fa-${toast.type === "success" ? "check-circle" : "exclamation-circle"}`} />
          {toast.message}
        </div>
      )}

      <div className="dashboard-body">
        {summary && !loading && (
          <div className="deposit-summary-cards">
            <div className="deposit-summary-card">
              <span className="deposit-summary-card__label">Currencies</span>
              <span className="deposit-summary-card__value">
                {formatNum(summary.currencies_count)}
              </span>
            </div>
            <div className="deposit-summary-card">
              <span className="deposit-summary-card__label">Total deposits</span>
              <span className="deposit-summary-card__value">
                {formatNum(summary.total_deposits)}
              </span>
            </div>
            <div className="deposit-summary-card">
              <span className="deposit-summary-card__label">Credited deposits</span>
              <span className="deposit-summary-card__value">
                {formatNum(summary.credited_deposits)}
              </span>
            </div>
          </div>
        )}

        <section className="deposits-section">
          <div className="deposits-section__header">
            <div className="deposits-section__title-wrap">
              <span className="deposits-section__icon">
                <i className="fas fa-coins" />
              </span>
              <h2 className="deposits-section__title">By Currency</h2>
            </div>
            <div className="deposits-section__stats deposits-section__stats--actions">
              <span className="deposits-stat">
                <strong>{byCurrency.length}</strong> currencies
              </span>
              <button
                type="button"
                className="deposits-export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading || !byCurrency.length}
                title="Download summary as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
              <button
                type="button"
                className="withdrawal-refresh-btn"
                onClick={fetchSummary}
                disabled={loading}
                title="Refresh"
              >
                <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="deposits-search-wrap deposit-summary-filters">
            <label className="deposit-summary-filter">
              <span>Status</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="deposits-search-input"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="deposit-summary-filter">
              <span>Credited</span>
              <select
                value={credited}
                onChange={(e) => setCredited(e.target.value)}
                className="deposits-search-input"
              >
                {CREDITED_OPTIONS.map((opt) => (
                  <option key={opt.value || "any"} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="deposit-summary-filter">
              <span>Asset</span>
              <input
                type="text"
                placeholder="e.g. USDT"
                value={assetId}
                onChange={(e) => setAssetId(e.target.value)}
                className="deposits-search-input"
              />
            </label>
            <label className="deposit-summary-filter">
              <span>Chain</span>
              <input
                type="text"
                placeholder="e.g. ETH"
                value={chainId}
                onChange={(e) => setChainId(e.target.value)}
                className="deposits-search-input"
              />
            </label>
            <label className="deposit-summary-filter">
              <span>From</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="deposits-search-input"
              />
            </label>
            <label className="deposit-summary-filter">
              <span>To</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="deposits-search-input"
              />
            </label>
          </div>

          {error && (
            <div className="deposits-error">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          <div className="deposits-table-wrap">
            <table className="deposits-table">
              <thead>
                <tr>
                  <th style={{ width: "2.5rem" }} />
                  <th>Currency</th>
                  <th>Total amount</th>
                  <th>Count</th>
                  <th>Credited</th>
                  <th>Chains</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="deposits-empty">
                      <i className="fas fa-spinner fa-spin" /> Loading…
                    </td>
                  </tr>
                ) : byCurrency.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="deposits-empty">
                      No deposit summary for these filters.
                    </td>
                  </tr>
                ) : (
                  byCurrency.map((row) => {
                    const currency = row.currency || "—";
                    const chains = Array.isArray(row.by_chain) ? row.by_chain : [];
                    const isOpen = !!expanded[currency];
                    return (
                      <React.Fragment key={currency}>
                        <tr>
                          <td>
                            {chains.length > 0 ? (
                              <button
                                type="button"
                                className="deposit-summary-expand"
                                onClick={() => toggleExpand(currency)}
                                aria-expanded={isOpen}
                                title={isOpen ? "Hide chains" : "Show chains"}
                              >
                                <i className={`fas fa-chevron-${isOpen ? "down" : "right"}`} />
                              </button>
                            ) : null}
                          </td>
                          <td>
                            <strong>{currency}</strong>
                          </td>
                          <td>{formatNum(row.total_amount)}</td>
                          <td>{formatNum(row.count)}</td>
                          <td>{formatNum(row.credited_count)}</td>
                          <td>{chains.length || "—"}</td>
                        </tr>
                        {isOpen &&
                          chains.map((ch) => (
                            <tr key={`${currency}-${ch.chain_id}`} className="deposit-summary-chain-row">
                              <td />
                              <td className="deposit-summary-chain-row__label">
                                <i className="fas fa-link" /> {ch.chain_id || "—"}
                              </td>
                              <td>{formatNum(ch.total_amount)}</td>
                              <td>{formatNum(ch.count)}</td>
                              <td>{formatNum(ch.credited_count)}</td>
                              <td>—</td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
};

export default DepositSummaryByCurrencyPage;
